/**
 * Lifecycle action panel — shown on the customer workspace and the
 * tenant_admin review page. Renders the current status, the last
 * relevant timestamp, and the appropriate primary CTA:
 *
 * IN_PROGRESS  → Submit for review (customer)
 * SUBMITTED    → Awaiting review banner (customer)
 * IN_REVIEW    → "Your contractor is reviewing" banner (customer)
 * APPROVED     → Pay deposit CTA OR "Deposit paid" badge (customer)
 * REJECTED     → Rejection reason banner (customer)
 * EXPIRED      → "Estimate expired" banner (customer)
 */

import { useState } from "react";

import { createDepositCheckout, submitEstimate } from "@starz/shared/api/sdk.gen";
import type { Estimate } from "@starz/shared/api/types.gen";

interface Props {
  estimate: Estimate;
  isAdminView: boolean;
  onSubmitted: (updated: Estimate) => void;
  onDepositRedirecting?: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  in_progress: "In progress",
  submitted: "Submitted",
  in_review: "Under review",
  info_requested: "Action needed",
  approved: "Approved",
  rejected: "Rejected",
  expired: "Expired",
};

const STATUS_PILL: Record<string, string> = {
  in_progress: "bg-starz-cream text-starz-charcoal",
  submitted: "bg-amber-50 text-amber-900",
  in_review: "bg-blue-50 text-blue-900",
  info_requested: "bg-violet-100 text-violet-900",
  approved: "bg-emerald-50 text-emerald-900",
  rejected: "bg-rose-50 text-rose-900",
  expired: "bg-warm-gray/20 text-warm-gray",
};

function formatCents(cents: number | null): string {
  if (cents == null) return "—";
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export function LifecyclePanel({
  estimate,
  isAdminView,
  onSubmitted,
  onDepositRedirecting,
}: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmitClick() {
    if (!window.confirm("Submit this estimate for review by your contractor?")) return;
    setSubmitting(true);
    setError(null);
    const result = await submitEstimate({ estimateId: estimate.estimate_id });
    setSubmitting(false);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    onSubmitted(result.data);
  }

  async function onPayClick() {
    setPaying(true);
    setError(null);
    const result = await createDepositCheckout({
      estimateId: estimate.estimate_id,
    });
    if ("error" in result) {
      setPaying(false);
      setError(result.error.error.message);
      return;
    }
    onDepositRedirecting?.();
    window.location.href = result.data.redirect_url;
  }

  const status = estimate.status;
  const pill = (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-widest ${STATUS_PILL[status] ?? ""}`}
    >
      {STATUS_LABEL[status] ?? status}
    </span>
  );

  return (
    <section
      aria-label="Estimate lifecycle"
      className="mb-6 rounded-xl border border-starz-gold/20 bg-white p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-warm-gray">Status</p>
          <div className="mt-1">{pill}</div>
        </div>
        {!isAdminView && status === "in_progress" && (
          <button
            type="button"
            onClick={onSubmitClick}
            disabled={submitting}
            className="min-h-[44px] rounded-lg bg-starz-navy px-5 py-3 text-sm font-semibold uppercase tracking-wide text-starz-off-white transition-opacity hover:bg-starz-navy/90 disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Submit for review"}
          </button>
        )}
        {!isAdminView && status === "approved" && estimate.deposit_paid_at == null && (
          <button
            type="button"
            onClick={onPayClick}
            disabled={paying || estimate.deposit_cents == null}
            className="min-h-[44px] rounded-lg bg-starz-gold px-5 py-3 text-sm font-semibold uppercase tracking-wide text-starz-navy transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {paying
              ? "Redirecting to Stripe…"
              : `Pay deposit · ${formatCents(estimate.deposit_cents)}`}
          </button>
        )}
      </div>
      {status === "submitted" && (
        <p className="mt-3 text-sm text-warm-gray">
          Thanks — your contractor was notified and will review shortly.
        </p>
      )}
      {status === "in_review" && (
        <p className="mt-3 text-sm text-warm-gray">Your contractor is reviewing this estimate.</p>
      )}
      {status === "approved" && estimate.deposit_paid_at != null && (
        <p className="mt-3 text-sm text-emerald-800">
          Deposit paid {new Date(estimate.deposit_paid_at).toLocaleDateString()}. The contractor
          will be in touch with next steps.
        </p>
      )}
      {status === "rejected" && (
        <div className="mt-3 text-sm text-rose-800">
          <strong>Not moving forward:</strong> {estimate.rejection_reason || "—"}
        </div>
      )}
      {status === "expired" && (
        <p className="mt-3 text-sm text-warm-gray">
          Expired {estimate.expired_at ? new Date(estimate.expired_at).toLocaleDateString() : ""}.
          Pricing changes over time, so this quote was closed out.
        </p>
      )}
      {error && (
        <p className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}
    </section>
  );
}
